/**
 * The mobile surface, v1 (docs/002, adr/0009, adr/0037). ADDITIVE ONLY — old builds live on
 * phones for months. Responses may gain fields and enum values; requests may gain OPTIONAL
 * fields. Anything else is v2. Guarded by `contract-shape.test.ts` (S48-lh45-TASK-005).
 * Clients must ignore unknown fields and unknown enum values.
 */
import { z } from 'zod';
import { roleAssignmentDTOSchema } from '../auth.js';
import { directoryMobilePhotoPathSchema, directoryPhotoQuerySchema } from '../employee-directory.js';
import { DEFAULT_PAGE_SIZE, MAX_PAGE_SIZE } from '../http.js';
import { localeCodeSchema, multiLanguageContentSchema } from '../i18n.js';
import { INVITE_CANCEL_SCOPES, INVITE_CODE_PATTERN, INVITE_RECURRENCE_FREQUENCIES, INVITE_SERIES_MIN_OCCURRENCES, inviteCoHostIdsSchema, inviteRecurrenceSchema, } from '../invite.js';
import { freeText, personName } from '../text-policy.js';
import { hostSearchQuerySchema } from '../visit.js';
import { WALK_IN_APPROVAL_DECISIONS, WALK_IN_APPROVAL_VIEW_STATUSES } from '../walk-in-approval.js';
export const MOBILE_API_VERSION = 1;
/** Suite modules the employee app can show. ADD ONLY. Clients ignore ids they do not know. */
export const MOBILE_MODULE_IDS = ['visitor'];
export const mobileModuleIdV1Schema = z.enum(MOBILE_MODULE_IDS);
export const MOBILE_MODULE_UNAVAILABLE_REASONS = ['no_permission', 'not_entitled'];
export const mobileModuleV1Schema = z.object({
    id: mobileModuleIdV1Schema,
    /** Enabled boolean feature keys of this module's app, e.g. 'visitor.mobile_app'. Strings, not
     *  FeatureKey: the web vocabulary may change without being a mobile v1 change. Sorted. */
    features: z.array(z.string().min(1)),
});
export const mobileUnavailableModuleV1Schema = z.object({
    id: mobileModuleIdV1Schema,
    reason: z.enum(MOBILE_MODULE_UNAVAILABLE_REASONS),
});
export const mobilePersonV1Schema = z.object({
    id: z.string().min(1),
    firstName: z.string().min(1),
    lastName: z.string().min(1),
    personType: z.enum(['EMPLOYEE', 'CONTRACTOR']),
    // The caller's own directory photo on THIS surface, or null; S47-rsq5-TASK-065, adr/0036 addendum.
    photoPath: directoryMobilePhotoPathSchema.nullable(),
});
export const mobileMeV1Schema = z.object({
    apiVersion: z.literal(MOBILE_API_VERSION),
    user: z.object({
        userId: z.string().min(1),
        email: z.string().email(),
        name: z.string().min(1),
        preferredLocale: localeCodeSchema,
    }),
    tenant: z.object({
        tenantId: z.string().min(1),
        tenantSlug: z.string().min(1),
        tenantName: z.string().min(1),
    }),
    roles: z.array(roleAssignmentDTOSchema),
    /** Sorted, de-duplicated — the same derivation as MeDTO.permissions. */
    permissions: z.array(z.string().min(1)),
    /** The employee this login is (adr/0036 decision 4), or null. Always present. */
    person: mobilePersonV1Schema.nullable(),
    /** The modules the shell shows, in MOBILE_MODULE_IDS order. Empty → the no-access state. */
    modules: z.array(mobileModuleV1Schema),
    /** Known modules the caller cannot use, with why. A module is in exactly one of the two lists. */
    unavailableModules: z.array(mobileUnavailableModuleV1Schema),
});
export const PUSH_PLATFORMS = ['IOS', 'ANDROID'];
/** Which transport the token belongs to. FAKE until the store phase (adr/0037 decision 7). */
export const PUSH_TRANSPORTS = ['APNS', 'FCM', 'FAKE'];
/** Path param: a client-generated UUID, one per app install, kept in secure storage. */
export const pushInstallationIdSchema = z.string().uuid();
export const registerPushInstallationV1Schema = z.object({
    platform: z.enum(PUSH_PLATFORMS),
    transport: z.enum(PUSH_TRANSPORTS),
    pushToken: z.string().min(1).max(4096),
    appVersion: z.string().min(1).max(64),
});
/** Response. NEVER carries the push token (S7). */
export const pushInstallationV1Schema = z.object({
    installationId: pushInstallationIdSchema,
    platform: z.enum(PUSH_PLATFORMS),
    transport: z.enum(PUSH_TRANSPORTS),
    appVersion: z.string().min(1),
    registeredAt: z.string().datetime(),
    lastSeenAt: z.string().datetime(),
});
/** The three one-tap answers Express offers. ADD ONLY. Every member is a VISIT_HOST_RESPONSES member. */
export const MOBILE_HOST_REPLIES = ['ON_MY_WAY', 'ASK_TO_WAIT', 'RUNNING_LATE'];
export const mobileHostReplyV1Schema = z.object({
    response: z.enum(MOBILE_HOST_REPLIES),
    respondedAt: z.string().datetime(),
});
/* ---- Co-hosts and series (S48-lh45 Phase 4b) ---- ADD ONLY ---- */
/** The caller's relationship to a visit or invite. Clients map an unknown value AND a missing field to HOST. */
export const MOBILE_HOST_ROLES = ['HOST', 'CO_HOST'];
/** A co-host, by name only (S7). ADD ONLY. */
export const mobileCoHostV1Schema = z.object({
    personId: z.string().min(1),
    displayName: z.string().min(1),
});
/** "Visit 2 of 4". ADD ONLY. */
export const mobileInviteSeriesV1Schema = z.object({
    seriesId: z.string().min(1),
    frequency: z.enum(INVITE_RECURRENCE_FREQUENCIES),
    occurrenceIndex: z.number().int().min(1),
    occurrenceCount: z.number().int().min(INVITE_SERIES_MIN_OCCURRENCES),
});
/** GET /visitor/hosts query — the same bounds as every host search. ADD ONLY. */
export const mobileHostSearchQueryV1Schema = hostSearchQuerySchema;
/** One colleague the caller may add as a co-host. Never the caller. No email/phone (S7). ADD ONLY. */
export const mobileHostCandidateV1Schema = z.object({
    personId: z.string().min(1),
    displayName: z.string().min(1),
    title: z.string().nullable(),
    departmentName: z.string().nullable(),
});
/** One occurrence's one-time code and pass link, for the share sheet (returned once). ADD ONLY. */
export const mobileIssuedOccurrenceV1Schema = z.object({
    inviteId: z.string().min(1),
    expectedFrom: z.string().datetime({ offset: true }),
    expectedTo: z.string().datetime({ offset: true }),
    code: z.string().regex(INVITE_CODE_PATTERN),
    passUrl: z.string().url(),
});
/** POST /visitor/invites/:id/cancel body. Only the invite's host may cancel. ADD ONLY. */
export const cancelMobileInviteV1Schema = z.object({ scope: z.enum(INVITE_CANCEL_SCOPES) });
/** What a cancel did. `cancelledInviteIds` is empty for an idempotent repeat. ADD ONLY. */
export const mobileCancelledInviteV1Schema = z.object({
    inviteId: z.string().min(1),
    seriesId: z.string().nullable(),
    cancelledInviteIds: z.array(z.string().min(1)),
});
/** GET /visitor/invites/:id/calendar — the HOST's own `.ics` text: no arrival code, no pass link (S7). ADD ONLY. */
export const mobileInviteCalendarV1Schema = z.object({
    filename: z.string().regex(/^[a-z0-9-]+\.ics$/),
    contentType: z.literal('text/calendar'),
    content: z.string().min(1),
});
/** A visit the caller hosts. Deliberately no visitor email/phone (S7: the host does not need them). ADD ONLY. */
export const mobileHostedVisitV1Schema = z.object({
    id: z.string().min(1),
    status: z.enum(['CHECKED_IN', 'CHECKED_OUT']),
    visitorFirstName: z.string().min(1),
    visitorLastName: z.string().min(1),
    visitorCompany: z.string().nullable(),
    purpose: z.string().nullable(),
    locationId: z.string().min(1),
    locationName: z.string().nullable(),
    checkedInAt: z.string().datetime(),
    checkedOutAt: z.string().datetime().nullable(),
    /** The latest answer, from the app or a chat button; null until one is given. Always present. */
    hostReply: mobileHostReplyV1Schema.nullable(),
    /** ADD ONLY. Missing → HOST. */
    role: z.enum(MOBILE_HOST_ROLES).optional(),
    /** The visit's host, so a co-host sees who invited the visitor. ADD ONLY. */
    hostName: z.string().nullable().optional(),
});
/** A pending, unexpired invite for the caller whose window starts in the requested day. ADD ONLY. */
export const mobileExpectedVisitorV1Schema = z.object({
    inviteId: z.string().min(1),
    visitorFirstName: z.string().min(1),
    visitorLastName: z.string().min(1),
    purpose: z.string().nullable(),
    locationId: z.string().min(1),
    locationName: z.string().min(1),
    expectedFrom: z.string().datetime(),
    expectedTo: z.string().datetime(),
    /** ADD ONLY. Missing → HOST. */
    role: z.enum(MOBILE_HOST_ROLES).optional(),
    hostName: z.string().min(1).optional(),
    series: mobileInviteSeriesV1Schema.nullable().optional(),
    coHosts: z.array(mobileCoHostV1Schema).optional(),
});
/** GET /visitor/today query: the phone's own local day as two instants, [from, to). ADD ONLY. */
export const mobileHostTodayQueryV1Schema = z.object({
    from: z.string().datetime(),
    to: z.string().datetime(),
});
/* ---- Walk-in approvals ---- */
/** A walk-in waiting for the caller's decision. Name, company and purpose only (S7). ADD ONLY. */
export const mobileWalkInApprovalV1Schema = z.object({
    id: z.string().min(1),
    status: z.enum(WALK_IN_APPROVAL_VIEW_STATUSES),
    visitorFirstName: z.string().min(1),
    visitorLastName: z.string().min(1),
    visitorCompany: z.string().nullable(),
    purpose: z.string().nullable(),
    locationId: z.string().min(1),
    locationName: z.string().min(1),
    requestedAt: z.string().datetime(),
    expiresAt: z.string().datetime(),
    decidedAt: z.string().datetime().nullable(),
});
/** ADD ONLY. */
export const mobileHostTodayV1Schema = z.object({
    /** false: this login is linked to no employee record, so it hosts nothing (both lists empty). */
    hostLinked: z.boolean(),
    generatedAt: z.string().datetime(),
    /** Checked in and not yet out, newest arrival first. At most 50. */
    onSite: z.array(mobileHostedVisitV1Schema),
    /** Expected in [from, to), earliest first. At most 50. */
    expected: z.array(mobileExpectedVisitorV1Schema),
    /** PENDING and unexpired, newest first. At most 50. ADD ONLY. */
    pendingApprovals: z.array(mobileWalkInApprovalV1Schema),
    /** true when any list was cut at 50. */
    isTruncated: z.boolean(),
});
/** ADD ONLY. */
export const setMobileHostReplyV1Schema = z.object({ response: z.enum(MOBILE_HOST_REPLIES) });
/** Push kinds the app routes on. ADD ONLY; the app ignores kinds it does not know. */
export const MOBILE_PUSH_KINDS = [
    'visitor.arrival',
    'visitor.walk_in_approval',
    'visitor.evacuation',
];
/** The push `data`: STRING values only (APNs custom keys and FCM data messages carry strings). ADD ONLY. */
export const mobilePushDataV1Schema = z.object({
    v: z.literal('1'),
    module: mobileModuleIdV1Schema,
    kind: z.enum(MOBILE_PUSH_KINDS),
    /** visitor.arrival → the visit id (GET /visitor/visits/:id); visitor.walk_in_approval → the approval id;
     *  visitor.evacuation → the alert id (the app opens the roll call; the id is informational). */
    subjectId: z.string().min(1),
});
/** What a push carries end to end: the lock-screen text plus `data`. ADD ONLY. */
export const mobilePushV1Schema = z.object({
    title: z.string().min(1).max(120),
    body: z.string().min(1).max(240),
    data: mobilePushDataV1Schema,
});
/* ---- Invites from the phone ---- ADD ONLY ---- */
/** ADD ONLY. */
export const mobileInviteVisitorTypeV1Schema = z.object({
    id: z.string().min(1),
    /** Tenant-authored label; the app shows its own locale, falling back to `en`. */
    label: multiLanguageContentSchema,
});
/** ADD ONLY. */
export const mobileInviteLocationV1Schema = z.object({
    id: z.string().min(1),
    name: z.string().min(1),
    /** IANA zone of the location — the app shows the window in it. */
    timezone: z.string().min(1),
    /** Offered here and active, in the kiosk's order. Never empty: a location with none is omitted. */
    visitorTypes: z.array(mobileInviteVisitorTypeV1Schema),
});
/** GET /visitor/invite-options — where the caller may invite a guest to. ADD ONLY. */
export const mobileInviteOptionsV1Schema = z.object({
    /** false: this login is linked to no employee record, so it cannot host (locations empty). */
    hostLinked: z.boolean(),
    /** Locations subscribed to Visitor, by name. At most 100. */
    locations: z.array(mobileInviteLocationV1Schema),
});
/** POST /visitor/invites — the host is always the caller; there is no host input (S1). ADD ONLY. */
export const createMobileInviteV1Schema = z.object({
    visitorFirstName: personName({ max: 100 }),
    visitorLastName: personName({ max: 100 }),
    visitorEmail: z.string().trim().email().max(254),
    locationId: z.string().min(1),
    visitorTypeId: z.string().trim().min(1).max(64),
    purpose: freeText({ max: 280 }).nullable().default(null),
    expectedFrom: z.string().datetime({ offset: true }),
    expectedTo: z.string().datetime({ offset: true }),
    /** Also email the Visit Pass to the visitor. The app's toggle defaults to on. */
    sendEmail: z.boolean(),
    /** Colleagues to co-host; never the caller. ADD ONLY. */
    coHostPersonIds: inviteCoHostIdsSchema.default([]),
    /** null = a one-off. ADD ONLY. */
    recurrence: inviteRecurrenceSchema.nullable().default(null),
});
/** The 201 body. `code` and `passUrl` are returned ONCE, for the share sheet; only hashes are stored. ADD ONLY. */
export const mobileIssuedInviteV1Schema = z.object({
    inviteId: z.string().min(1),
    visitorFirstName: z.string().min(1),
    visitorLastName: z.string().min(1),
    locationName: z.string().min(1),
    expectedFrom: z.string().datetime({ offset: true }),
    expectedTo: z.string().datetime({ offset: true }),
    code: z.string().min(1),
    passUrl: z.string().url(),
    emailed: z.boolean(),
    seriesId: z.string().nullable().optional(),
    /** Every occurrence, first first; length 1 for a one-off. ADD ONLY. */
    occurrences: z.array(mobileIssuedOccurrenceV1Schema).optional(),
    coHosts: z.array(mobileCoHostV1Schema).optional(),
});
/* ---- History ---- */
/** GET /visitor/visits — the caller's own visits, newest arrival first. `{ data, meta }`. ADD ONLY. */
export const mobileHostedVisitListQueryV1Schema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(DEFAULT_PAGE_SIZE),
});
/** ADD ONLY. */
export const decideMobileWalkInApprovalV1Schema = z.object({
    decision: z.enum(WALK_IN_APPROVAL_DECISIONS),
});
/* ---- Host preferences ---- */
/** ADD ONLY. */
export const mobileHostPreferencesV1Schema = z.object({
    /** Ask me before a walk-in for me checks in. Default false. */
    requireWalkInApproval: z.boolean(),
    /** false while the tenant has Express arrival pushes off — the preference then has no effect. */
    walkInApprovalAvailable: z.boolean(),
});
/** ADD ONLY. */
export const updateMobileHostPreferencesV1Schema = z.object({ requireWalkInApproval: z.boolean() });
/* ---- Evacuation roll call ---- */
/** One of the caller's visitors on site during an evacuation. Name, company, where, when — nothing else (S7). ADD ONLY. */
export const mobileRollCallEntryV1Schema = z.object({
    /** The VisitorVisit id — the id POST /visitor/visits/:id/mark-safe takes. */
    visitId: z.string().min(1),
    visitorFirstName: z.string().min(1),
    visitorLastName: z.string().min(1),
    visitorCompany: z.string().nullable(),
    locationId: z.string().min(1),
    locationName: z.string().nullable(),
    checkedInAt: z.string().datetime(),
    /** When this visitor was accounted for (by anyone: the host, a warden), or null while outstanding. */
    markedSafeAt: z.string().datetime().nullable(),
});
/** GET /visitor/roll-call — the caller's visitors checked in now, outstanding first. ADD ONLY. */
export const mobileRollCallV1Schema = z.object({
    /** false: this login is linked to no employee record, so it hosts nothing (entries empty). */
    hostLinked: z.boolean(),
    generatedAt: z.string().datetime(),
    /** Counted over every match, not entries.length. */
    total: z.number().int().nonnegative(),
    safeCount: z.number().int().nonnegative(),
    /** true when total exceeded 100 and entries is a prefix. */
    isTruncated: z.boolean(),
    entries: z.array(mobileRollCallEntryV1Schema),
});
/** Every /api/mobile/v1 endpoint. A route must be here (S48-lh45-TASK-009 asserts it at startup). */
export const MOBILE_V1_ENDPOINTS = {
    // Binary body (an image): no `response` schema; `request` is the query (S47-rsq5-TASK-064).
    'GET /api/mobile/v1/directory/people/:id/photo/:fileId': { request: directoryPhotoQuerySchema },
    'GET /api/mobile/v1/me': { response: mobileMeV1Schema },
    'PUT /api/mobile/v1/push/installations/:installationId': {
        request: registerPushInstallationV1Schema,
        response: pushInstallationV1Schema,
    },
    'DELETE /api/mobile/v1/push/installations/:installationId': {},
    'GET /api/mobile/v1/visitor/hosts': {
        request: mobileHostSearchQueryV1Schema,
        response: z.array(mobileHostCandidateV1Schema),
    },
    'GET /api/mobile/v1/visitor/invite-options': { response: mobileInviteOptionsV1Schema },
    'GET /api/mobile/v1/visitor/invites/:id/calendar': { response: mobileInviteCalendarV1Schema },
    'GET /api/mobile/v1/visitor/preferences': { response: mobileHostPreferencesV1Schema },
    'GET /api/mobile/v1/visitor/roll-call': { response: mobileRollCallV1Schema },
    'GET /api/mobile/v1/visitor/today': {
        request: mobileHostTodayQueryV1Schema,
        response: mobileHostTodayV1Schema,
    },
    // `response` is the `data` array; the standard `{ page, pageSize, total }` `meta` is not part of the snapshot.
    'GET /api/mobile/v1/visitor/visits': {
        request: mobileHostedVisitListQueryV1Schema,
        response: z.array(mobileHostedVisitV1Schema),
    },
    'GET /api/mobile/v1/visitor/visits/:id': { response: mobileHostedVisitV1Schema },
    'GET /api/mobile/v1/visitor/walk-in-approvals/:id': { response: mobileWalkInApprovalV1Schema },
    'POST /api/mobile/v1/visitor/invites': {
        request: createMobileInviteV1Schema,
        response: mobileIssuedInviteV1Schema,
    },
    'POST /api/mobile/v1/visitor/invites/:id/cancel': {
        request: cancelMobileInviteV1Schema,
        response: mobileCancelledInviteV1Schema,
    },
    'POST /api/mobile/v1/visitor/visits/:id/check-out': { response: mobileHostedVisitV1Schema },
    'POST /api/mobile/v1/visitor/visits/:id/mark-safe': { response: mobileRollCallEntryV1Schema },
    'PUT /api/mobile/v1/visitor/preferences': {
        request: updateMobileHostPreferencesV1Schema,
        response: mobileHostPreferencesV1Schema,
    },
    'PUT /api/mobile/v1/visitor/visits/:id/host-reply': {
        request: setMobileHostReplyV1Schema,
        response: mobileHostedVisitV1Schema,
    },
    'PUT /api/mobile/v1/visitor/walk-in-approvals/:id/decision': {
        request: decideMobileWalkInApprovalV1Schema,
        response: mobileWalkInApprovalV1Schema,
    },
};
//# sourceMappingURL=v1.js.map